// components/AgentsGrid.js
import React, { useState, useEffect, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { useNavigate } from "react-router-dom";

// Custom Header Component with styled tooltips
// Update the CustomHeaderComponent to handle sorting
const CustomHeaderComponent = (props) => {
	const tooltipContent = props.column.getColDef().tooltipContent;

	const onSortRequested = (event) => {
		// Get current sort state
		const currentSort = props.column.getSort();

		// Toggle between asc, desc, and null (no sort)
		let nextSort;
		if (currentSort === 'asc') {
			nextSort = 'desc';
		} else if (currentSort === 'desc') {
			nextSort = null;
		} else {
			nextSort = 'asc';
		}

		props.setSort(nextSort, event.shiftKey);
	};

	// Get sort icon based on current state
	let sortIcon = null;
	if (props.column.isSortAscending()) {
		sortIcon = <span className="ag-header-icon ag-sort-ascending-icon">↑</span>;
	} else if (props.column.isSortDescending()) {
		sortIcon = <span className="ag-header-icon ag-sort-descending-icon">↓</span>;
	}

	if (!tooltipContent) {
		return (
			<div className="ag-header-cell-label" onClick={onSortRequested}>
				<span className="ag-header-cell-text">{props.displayName}</span>
				{sortIcon}
			</div>
		);
	}

	return (
		<Tippy
			content={tooltipContent}
			placement="bottom"
			arrow={true}
			duration={[200, 200]}
			className="custom-tooltip"
			appendTo={document.body}
			interactive={true}
		>
			<div className="ag-header-cell-label flex items-center" onClick={onSortRequested}>
				<span className="ag-header-cell-text">{props.displayName}</span>
				{sortIcon}
			</div>
		</Tippy>
	);
};

const getColorClass = (value) => {
	if (value > 0.7) return "bg-green-500";
	if (value > 0.6) return "bg-lime-300";
	if (value > 0.5) return "bg-yellow-500";
	if (value > 0.4) return "bg-orange-500";
	return "bg-red-500";
};

const PerformanceCell = (params) => {
	const tooltipContent = (
		<div>
			<div className="tooltip-title">{params.colDef.headerName}</div>
			<div>{params.value ? params.value.toFixed(3) : 0}</div>
			<div className="tooltip-range">Valid range: 0 to 1</div>
			<div className="tooltip-description">{params.colDef.tooltipDescription}</div>
		</div>
	);

	return (
		<Tippy
			content={tooltipContent}
			className="custom-tooltip"
			duration={[200, 400]}
			delay={100}
		>
			<div className="flex items-center justify-center w-full h-full py-4">
				<div className="bg-gray-200 w-2/5 h-2 rounded-full">
					<div
						className={`${getColorClass(params.value)} h-full rounded-full`}
						style={{ width: `${params.value * 100}%` }}
					></div>
				</div>
			</div>
		</Tippy>
	);
};

const getSentimentColorClassAndLabel = (value) => {
	if (value > 0.5)
		return { colorClass: "bg-green-500", label: "VERY POSITIVE" };
	if (value > 0.25) return { colorClass: "bg-green-300", label: "POSITIVE" };
	if (value > -0.25) return { colorClass: "bg-yellow-300", label: "NEUTRAL" };
	if (value > -0.5) return { colorClass: "bg-orange-500", label: "NEGATIVE" };
	return { colorClass: "bg-red-700", label: "VERY NEGATIVE" };
};

const SentimentCell = (params) => {
	const { colorClass, label } = getSentimentColorClassAndLabel(params.value);

	const tooltipContent = (
		<div>
			<div className="tooltip-title">Sentiment Score</div>
			<div>{label}: {params.value ? params.value.toFixed(3) : 0}</div>
			<div className="tooltip-range">Valid range: -1 to 1</div>
			<div className="tooltip-description">Average emotional tone of the agent during calls</div>
		</div>
	);

	return (
		<Tippy
			content={tooltipContent}
			className="custom-tooltip"
			duration={[200, 400]}
			delay={100}
		>
			<div className="flex items-center justify-center w-full h-full py-4">
				<div className="bg-gray-200 w-2/5 h-2 rounded-full">
					<div
						className={`${colorClass} h-full rounded-full`}
						style={{ width: `${Math.abs(params.value) * 100}%` }}
					></div>
				</div>
			</div>
		</Tippy>
	);
};

const AgentsGrid = ({ allAgentInfo, filters }) => {
	const [rowData, setRowData] = useState([]);
	const navigate = useNavigate();

	const qualityManagementEnabled = window.appSettings?.qualityManagementEnabled;

	const handleAgentClick = (agent) => {
		navigate(`/agent/${encodeURIComponent(agent.agent)}`, {
			state: { referrer: "/agents", filters, agent },
		});
	};

	const AgentLinkCell = (params) => (
		<div className="text-right">
			<span
				onClick={() => handleAgentClick(params.data)}
				className="text-blue-600 hover:underline cursor-pointer"
			>
				{params.value}
			</span>
		</div>
	);

	const QueueCellRenderer = (props) => {
		if (!props.value) return null;

		let obj = {};
		try {
			obj = JSON.parse(props.value);
		} catch (error) {
			console.error(error);
			return null;
		}

		const sortedQueues = Object.entries(obj).sort((a, b) => b[1] - a[1]);

		const tooltipContent = (
			<div>
				<div className="tooltip-title">Queue Distribution</div>
				{sortedQueues.map(([queueName, count], index) => (
					<div key={index} className="mb-2">
						<div className="font-medium">{queueName}</div>
						<div className="text-xs text-gray-400">Call Count: {count}</div>
					</div>
				))}
			</div>
		);

		return (
			<Tippy
				content={tooltipContent}
				interactive={true}
				appendTo={document.body}
				placement="top"
				maxWidth={400}
				className="custom-tooltip"
				duration={[200, 400]}
				delay={100}
			>
				<div className="cursor-pointer">
					{sortedQueues.map(([queueName, count], index) => (
						<span
							key={index}
							className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded inline-block"
						>
							{queueName} ({count})
						</span>
					))}
				</div>
			</Tippy>
		);
	};

	const SilenceCellRenderer = (params) => {
		const tooltipContent = (
			<div>
				<div className="tooltip-title">Average Silence Percentage</div>
				<div>{params.value ? params.value.toFixed(2) : 0}% of call time</div>
				<div className="tooltip-description">
					Percentage of call time where neither party is speaking
				</div>
			</div>
		);

		return (
			<Tippy
				content={tooltipContent}
				className="custom-tooltip"
				duration={[200, 400]}
				delay={100}
			>
				<div className="text-right">
					{params.value ? params.value.toFixed(2) : 0}%
				</div>
			</Tippy>
		);
	};


	const HandleTimeCellRenderer = (params) => {
		const tooltipContent = (
			<div>
				<div className="tooltip-title">Average Handle Time</div>
				<div>{params.value}s</div>
				<div className="tooltip-description">
					Average duration of calls handled by this agent
				</div>
			</div>
		);

		return (
			<Tippy
				content={tooltipContent}
				className="custom-tooltip"
				duration={[200, 400]}
				delay={100}
			>
				<div className="text-right">
					{params.value}s
				</div>
			</Tippy>
		);
	};

	const TotalCallsCellRenderer = (params) => {
		const tooltipContent = (
			<div>
				<div className="tooltip-title">Total Calls</div>
				<div>{params.value} calls</div>
				<div className="tooltip-description">
					Total number of calls handled by this agent in the selected period
				</div>
			</div>
		);

		return (
			<Tippy
				content={tooltipContent}
				className="custom-tooltip"
				duration={[200, 400]}
				delay={100}
			>
				<div className="text-right">
					{params.value}
				</div>
			</Tippy>
		);
	};

	const QMScoreCellRenderer = (params) => {
		const tooltipContent = (
			<div>
				<div className="tooltip-title">Average QM Score</div>
				<div>{params.value ? params.value.toFixed(0) : 0}/100</div>
				<div className="tooltip-range">Valid range: 0 to 100</div>
				<div className="tooltip-description">
					Average quality management evaluation score for the agent
				</div>
			</div>
		);

		return (
			<Tippy
				content={tooltipContent}
				className="custom-tooltip"
				duration={[200, 400]}
				delay={100}
			>
				<div className="text-right">
					{params.value ? params.value.toFixed(0) : 0}
				</div>
			</Tippy>
		);
	};

	const AutofailRateCellRenderer = (params) => {
		const tooltipContent = (
			<div>
				<div className="tooltip-title">Average Autofail Rate</div>
				<div>{params.value ? (params.value * 100).toFixed(2) : 0}% of calls</div>
				<div className="tooltip-range">Valid range: 0% to 100%</div>
				<div className="tooltip-description">
					Average rate at which calls autofail quality management checks
				</div>
			</div>
		);

		return (
			<Tippy
				content={tooltipContent}
				className="custom-tooltip"
				duration={[200, 400]}
				delay={100}
			>
				<div className="text-right">
					{params.value ? (params.value * 100).toFixed(2) : 0}%
				</div>
			</Tippy>
		);
	};

	let columns = [
		{
			headerName: "Agent",
			field: "agent",
			sortable: true,
			filter: false,
			width: 170,
			cellRenderer: AgentLinkCell,
			headerClass: "text-right",
			cellClass: "text-right",
			headerComponent: CustomHeaderComponent,
			tooltipContent: (
				<div>
					<div className="tooltip-title">Agent Name</div>
					<div className="tooltip-description">Name of the customer service representative</div>
				</div>
			),
		},
		{
			headerName: "Total Calls",
			field: "totalCalls",
			width: 100,
			sortable: true,
			filter: false,
			headerClass: "text-right",
			cellClass: "text-right",
			cellRenderer: TotalCallsCellRenderer,
			headerComponent: CustomHeaderComponent,
			tooltipContent: (
				<div>
					<div className="tooltip-title">Total Calls</div>
					<div className="tooltip-description">Total number of calls handled by this agent</div>
				</div>
			),
		},
		{
			headerName: "Avg Professionalism",
			field: "averageProfessionalism",
			width: 160,
			sortable: true,
			filter: false,
			cellRenderer: PerformanceCell,
			headerClass: "text-center",
			cellClass: "text-center",
			tooltipDescription: "Measure of agent's professional conduct across all calls",
			headerComponent: CustomHeaderComponent,
			tooltipContent: (
				<div>
					<div className="tooltip-title">Average Professionalism</div>
					<div className="tooltip-description">Measure of agent's professional conduct across all calls</div>
					<div className="tooltip-range">Valid range: 0 (poor) to 1 (excellent)</div>
				</div>
			),
		},
		{
			headerName: "Avg Sentiment",
			field: "averageSentiment",
			width: 140,
			sortable: true,
			filter: false,
			cellRenderer: SentimentCell,
			headerClass: "text-center",
			cellClass: "text-center",
			headerComponent: CustomHeaderComponent,
			tooltipContent: (
				<div>
					<div className="tooltip-title">Average Sentiment</div>
					<div className="tooltip-description">Average emotional tone of the agent during calls</div>
					<div className="tooltip-range">Valid range: -1 (very negative) to 1 (very positive)</div>
				</div>
			),
		},
		{
			headerName: "Avg Silence %",
			field: "averageSilencePercent",
			width: 130,
			sortable: true,
			filter: false,
			cellRenderer: SilenceCellRenderer,
			headerClass: "text-right",
			cellClass: "text-right",
			headerComponent: CustomHeaderComponent,
			tooltipContent: (
				<div>
					<div className="tooltip-title">Average Silence Percentage</div>
					<div className="tooltip-description">Percentage of call time where neither party is speaking</div>
				</div>
			),
		},
		{
			headerName: "Avg Customer Satisfaction",
			field: "averageCustomerSatisfaction",
			width: 200,
			sortable: true,
			filter: false,
			cellRenderer: PerformanceCell,
			headerClass: "text-right",
			cellClass: "text-right",
			tooltipDescription: "Average satisfaction level of customers after interacting with this agent",
			headerComponent: CustomHeaderComponent,
			tooltipContent: (
				<div>
					<div className="tooltip-title">Average Customer Satisfaction</div>
					<div className="tooltip-description">Average satisfaction level of customers after interacting with this agent</div>
					<div className="tooltip-range">Valid range: 0 (very unsatisfied) to 1 (very satisfied)</div>
				</div>
			),
		},
		{
			headerName: "Avg Handle Time",
			field: "averageCallDuration",
			width: 150,
			sortable: true,
			filter: false,
			cellRenderer: HandleTimeCellRenderer,
			headerClass: "text-right",
			cellClass: "text-right",
			headerComponent: CustomHeaderComponent,
			tooltipContent: (
				<div>
					<div className="tooltip-title">Average Handle Time</div>
					<div className="tooltip-description">Average duration of calls handled by this agent</div>
				</div>
			),
		}
	];

	const qmColumns = qualityManagementEnabled ? [{
		headerName: "Avg QM Score",
		field: "avg_qm_score",
		width: 120,
		sortable: true,
		filter: false,
		cellRenderer: QMScoreCellRenderer,
		headerClass: "text-right",
		cellClass: "text-right",
		headerComponent: CustomHeaderComponent,
		tooltipContent: (
			<div>
				<div className="tooltip-title">Average Quality Management Score</div>
				<div className="tooltip-description">Average quality management evaluation score for the agent</div>
				<div className="tooltip-range">Valid range: 0 to 100</div>
			</div>
		)
	}, {
		headerName: "Autofail Rate",
		field: "avg_autofail_rate",
		width: 117,
		sortable: true,
		filter: false,
		cellRenderer: AutofailRateCellRenderer,
		headerClass: "text-right",
		cellClass: "text-right",
		headerComponent: CustomHeaderComponent,
		tooltipContent: (
			<div>
				<div className="tooltip-title">Average Autofail Rate</div>
				<div className="tooltip-description">Average rate at which calls autofail quality management checks</div>
				<div className="tooltip-range">Valid range: 0% to 100%</div>
			</div>
		)
	}] : [];

	columns = [...columns, ...qmColumns];

	columns.push(
		{
			headerName: "Queues",
			field: "callsByQueue",
			minWidth: 200,
			flex: 3,
			cellRenderer: QueueCellRenderer,
			headerComponent: CustomHeaderComponent,
			tooltipContent: (
				<div>
					<div className="tooltip-title">Queues</div>
					<div className="tooltip-description">Distribution of call queues handled by this agent</div>
				</div>
			),
		}
	);

	const onGridReady = (params) => {
		params.api.sizeColumnsToFit();
	};

	useEffect(() => {
		console.log("GRID UPDATE", allAgentInfo);
		if (allAgentInfo && allAgentInfo.length > 0) {
			const sortedAgents = [...allAgentInfo].sort(
				(a, b) => b.totalCalls - a.totalCalls
			);
			setRowData(sortedAgents);
		}
	}, [allAgentInfo]);

	const defaultColDef = useMemo(() => ({
		resizable: true,
	}), []);

	const gridOptions = {
		pagination: true,
		paginationPageSize: 20,
		defaultColDef: defaultColDef,
	};

	return (
		<div className="ag-theme-alpine" style={{ height: 1000, width: "100%" }}>
			<AgGridReact
				columnDefs={columns}
				rowData={rowData}
				onGridReady={onGridReady}
				gridOptions={gridOptions}
				enableCellTextSelection={true}
				ensureDomOrder={true}
			/>
		</div>
	);
};

export default AgentsGrid;